import { SurveyRecoveryItem, surveyCanonical } from './recovery';
import { SurveyAnswer, SurveyPresentation } from './types';

export interface RecoveredSurveyAnswer {
  eventId: string;
  presentation: SurveyPresentation;
  answer: SurveyAnswer;
  answerText: string;
  recoverySequence: number;
}

export interface RecoveredSurveyTimelineItem {
  eventId: string;
  presentation: SurveyPresentation;
  answerText?: string;
  helpExchanges: NonNullable<SurveyRecoveryItem['helpExchange']>[];
  isCurrent: boolean;
}

export interface SurveyRecoveryProjection {
  answered: RecoveredSurveyAnswer[];
  timeline: RecoveredSurveyTimelineItem[];
  currentPending: boolean;
}

export const hasAnchoredRecoveredCurrent = (items: SurveyRecoveryItem[], current: SurveyPresentation) => {
  const final = items[items.length - 1];
  return !!final?.isCurrent && final.presentation.formId === current.formId &&
    final.presentation.presentationRevision === current.presentationRevision &&
    final.presentation.surveyId === current.surveyId &&
    final.presentation.surveyVersion === current.surveyVersion;
};

export const formatRecoveredAnswer = (presentation: SurveyPresentation, answer: SurveyAnswer): string | undefined => {
  const optionIds = presentation.options.map((option) => option.id);
  if (new Set(optionIds).size !== optionIds.length) return undefined;
  if (answer.type === 'freeText') return presentation.messageType === 'surveyQuestion' &&
    presentation.inputType === 'freeText' ? answer.text : undefined;
  if (answer.type === 'singleChoice' || answer.type === 'route') {
    if (answer.type === 'singleChoice' && (presentation.messageType !== 'surveyQuestion' || presentation.inputType !== 'singleChoice') ||
        answer.type === 'route' && presentation.messageType !== 'surveyRoute') return undefined;
    return presentation.options.find((option) => option.id === answer.choiceId)?.label;
  }
  if (answer.type === 'multiChoice') {
    if (presentation.messageType !== 'surveyQuestion' || presentation.inputType !== 'multiChoice' ||
        new Set(answer.choiceIds).size !== answer.choiceIds.length ||
        answer.choiceIds.some((choiceId) => !optionIds.includes(choiceId))) return undefined;
    const selected = new Set(answer.choiceIds);
    return presentation.options.filter((option) => selected.has(option.id)).map((option) => option.label).join(', ');
  }
  if (answer.type === 'nonAnswer') {
    const outcomes = presentation.nonAnswerOptions.map((option) => option.outcome);
    if (new Set(outcomes).size !== outcomes.length) return undefined;
    return presentation.nonAnswerOptions.find((option) => option.outcome === answer.outcome)?.label;
  }
  if (answer.type === 'consent' && presentation.messageType === 'surveyConsent' && presentation.consent) {
    return answer.accepted ? presentation.consent.acceptedLabel : presentation.consent.declinedLabel;
  }
  return undefined;
};

const project = (items: SurveyRecoveryItem[]): SurveyRecoveryProjection | undefined => {
  if (!items.length || items.some((item, index) => item.isCurrent !== (index === items.length - 1))) return undefined;
  const current = items[items.length - 1].presentation;
  if (items.some((item) => item.presentation.surveyId !== current.surveyId ||
      item.presentation.surveyVersion !== current.surveyVersion)) return undefined;
  const answered: RecoveredSurveyAnswer[] = [];
  const timeline: RecoveredSurveyTimelineItem[] = [];
  const helpExchanges = new Map<string, NonNullable<SurveyRecoveryItem['helpExchange']>>();
  for (const [index, item] of items.entries()) {
    const answer = item.result?.acceptedAnswer;
    const answerText = answer ? formatRecoveredAnswer(item.presentation, answer) : undefined;
    const replacementIndex = items.findIndex((candidate) =>
      candidate.presentation.formId === item.result?.replacementFormId);
    const replacement = items[replacementIndex]?.presentation;
    const hasReplacementLink = item.result && (item.result.replacementFormId !== undefined ||
      item.result.replacementRevision !== undefined);
    if (hasReplacementLink && (item.result!.replacementFormId === undefined ||
        item.result!.replacementRevision === undefined ||
        replacement && (replacementIndex !== index + 1 ||
          item.result!.replacementRevision !== replacement.presentationRevision))) return undefined;
    if (index < items.length - 1 && ((!answer || answerText === undefined) && !item.helpExchange) ||
        index === items.length - 1 && item.result) return undefined;
    const duplicateHelp = item.helpExchange && helpExchanges.get(item.helpExchange.interactionId);
    if (duplicateHelp && surveyCanonical(duplicateHelp) !== surveyCanonical(item.helpExchange)) return undefined;
    if (item.helpExchange && !duplicateHelp) helpExchanges.set(item.helpExchange.interactionId, item.helpExchange);
    const previous = timeline[timeline.length - 1];
    const sameBlock = previous && previous.presentation.surveyId === item.presentation.surveyId &&
      previous.presentation.surveyVersion === item.presentation.surveyVersion &&
      previous.presentation.blockId === item.presentation.blockId &&
      previous.presentation.messageType === item.presentation.messageType;
    if (sameBlock) {
      previous.eventId = item.eventId;
      previous.presentation = item.presentation;
      previous.answerText = answerText ?? previous.answerText;
      if (item.helpExchange && !duplicateHelp) previous.helpExchanges.push(item.helpExchange);
      previous.isCurrent = item.isCurrent;
    } else {
      timeline.push({ eventId: item.eventId, presentation: item.presentation, answerText,
        helpExchanges: item.helpExchange && !duplicateHelp ? [item.helpExchange] : [], isCurrent: item.isCurrent });
    }
    if (answer && answerText !== undefined) answered.push({ eventId: item.eventId, presentation: item.presentation,
      answer, answerText, recoverySequence: item.recoverySequence });
  }
  return { answered, timeline, currentPending: !!items[items.length - 1].pendingInteractionId };
};

export const buildAnchoredSurveyRecovery = (items: SurveyRecoveryItem[], current: SurveyPresentation) => {
  if (!hasAnchoredRecoveredCurrent(items, current)) return undefined;
  return project(items);
};

export const buildTerminalSurveyRecovery = (items: SurveyRecoveryItem[]) => project(items);